import { useState } from 'react'
import { supabase } from '../supabaseClient'
import { useNavigate } from 'react-router-dom'

export default function Signup() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [agreedToConsent, setAgreedToConsent] = useState(false)
  const navigate = useNavigate()

  async function handleSignup(e) {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { data: clinic } = await supabase.from('clinics').select('id').eq('invite_code', inviteCode).single()
    if (!clinic) { setError('Invalid invite code. Please check with your clinician.'); setLoading(false); return }

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: 'https://completept.app/login' }
    })
    if (authError) { setError(authError.message); setLoading(false); return }

    await supabase.from('users').insert({
      id: authData.user.id,
      full_name: fullName,
      role: 'patient',
      clinic_id: clinic.id
    })

    setSent(true)
    setLoading(false)
  }

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #3730a3 100%)', padding: '1.5rem', paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}>
      <div style={{ width: '100%', maxWidth: 420, background: 'white', borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '2rem 2rem 1.5rem', background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
          <p style={{ fontWeight: 800, fontSize: 20, color: 'white', letterSpacing: -0.5, margin: '0 0 1rem' }}>Complete PT</p>
          <p style={{ fontSize: 24, fontWeight: 700, color: 'white', lineHeight: 1.2, letterSpacing: -0.5, margin: '0 0 0.5rem' }}>Create your account</p>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0 }}>Join your clinic's exercise program</p>
        </div>

        <div style={{ padding: '2rem' }}>
          {sent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: '1rem' }}>📧</div>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 16, marginBottom: 8 }}>Check your email</p>
              <p style={{ fontSize: 14, marginBottom: '1.5rem' }}>We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account.</p>
              <button className="secondary" onClick={() => navigate('/login')} style={{ width: '100%', padding: 14 }}>
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              {error && <div className="alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
              <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-field">
                  <label className="form-label">Full name</label>
                  <input placeholder="Jane Smith" value={fullName} onChange={e => setFullName(e.target.value)} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Email address</label>
                  <input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Password</label>
                  <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Clinic invite code</label>
                  <input placeholder="e.g. de7b18c2" value={inviteCode} onChange={e => setInviteCode(e.target.value)} required style={{ letterSpacing: 1 }} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={agreedToConsent} onChange={e => setAgreedToConsent(e.target.checked)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                  <div style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 4, border: agreedToConsent ? 'none' : '1.5px solid #d1d5db', background: agreedToConsent ? 'var(--brand)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                    {agreedToConsent && <svg width="11" height="8" viewBox="0 0 11 8" fill="none"><path d="M1 4L4 7L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    I have read and agree to the{' '}
                    <a href="/consent" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', fontWeight: 600 }}>Patient Consent and Rights</a>
                  </span>
                </label>
                <button type="submit" disabled={loading || !agreedToConsent} style={{ marginTop: 4, padding: 14, fontSize: 15 }}>
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </form>
              <div style={{ borderTop: '1px solid var(--border)', marginTop: '1.5rem', paddingTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ textAlign: 'center', margin: 0 }}>
                  Already have an account?{' '}
                  <span onClick={() => navigate('/login')} style={{ color: 'var(--brand)', fontWeight: 600, cursor: 'pointer' }}>Sign in</span>
                </p>
                <p style={{ textAlign: 'center', margin: 0 }}>
                  Are you a clinician?{' '}
                  <span onClick={() => navigate('/signup/clinician')} style={{ color: 'var(--brand)', fontWeight: 600, cursor: 'pointer' }}>Set up your clinic</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
